import React, { useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  IconButton,
  Divider,
  TextField,
  Autocomplete,
  InputAdornment,
  Chip,
  FormControl,
  RadioGroup,
  FormControlLabel,
  Radio,
  Checkbox,
  Alert,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Collapse,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import CloseIcon from '@mui/icons-material/Close';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import LocalHospitalIcon from '@mui/icons-material/LocalHospital';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import LockIcon from '@mui/icons-material/Lock';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import EditIcon from '@mui/icons-material/Edit';
import StorageIcon from '@mui/icons-material/Storage';
import { useTranslation } from 'react-i18next';
import { erzwungenerStatusfilter } from '../utils/helpers';

const PanelContainer = styled(Paper)(({ theme }) => ({
  width: '100%',
  maxHeight: '500px',
  overflowY: 'auto',
  marginTop: theme.spacing(1),
}));

const Header = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  padding: theme.spacing(2),
  borderBottom: `1px solid ${theme.palette.divider}`,
  position: 'sticky',
  top: 0,
  backgroundColor: theme.palette.background.paper,
  zIndex: 1,
}));

const Content = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2),
}));

/**
 * Filter Expansion Panel Component
 * Two-level expandable filter panel that appears below the filter button
 *
 * Level 1: Filter type selection (Categories, Stations, Sensitivity, OMOP)
 * Level 2: Filter options for selected type
 *
 * @param {boolean} open - Whether panel is open
 * @param {Function} onClose - Close handler
 * @param {Array} selectedTags - Selected tags
 * @param {Function} setSelectedTags - Setter for tags
 * @param {Array} selectedStations - Selected stations
 * @param {Function} setSelectedStations - Setter for stations
 * @param {string} selectedSensitivity - Selected sensitivity
 * @param {Function} setSelectedSensitivity - Setter for sensitivity
 * @param {string} selectedOmopStatus - Selected OMOP status
 * @param {string} selectedOpenehrStatus - Gewaehlter openEHR-Status (#782)
 * @param {Function} setSelectedOpenehrStatus - Setzt den openEHR-Status
 * @param {Map|null} omopStatusCounts - Anzahl je OMOP-Status (#782 AK3c), null wenn unbekannt
 * @param {Map|null} openehrStatusCounts - Anzahl je openEHR-Status (#782 AK3c), null wenn unbekannt
 * @param {Function} setSelectedOmopStatus - Setter for OMOP status
 * @param {Array} allTags - All available tags
 * @param {Array} popularTags - Popular tags for quick selection
 * @param {Array} allStations - All available stations
 * @param {Array} allStationsWithCategories - Stations with category/building info
 * @param {Array} popularStations - Popular stations for quick selection
 * @param {Array} savedSets - Saved filter sets
 * @param {Function} handleLoadSet - Handler for loading saved set
 * @param {Function} handleTagToggle - Handler for tag toggle
 * @param {Function} setTagDialogOpen - Handler to open tag management dialog
 */
const FilterExpansionPanel = ({
  open,
  onClose,
  selectedTags,
  setSelectedTags,
  selectedStations,
  setSelectedStations,
  selectedSensitivity,
  setSelectedSensitivity,
  selectedOmopStatus,
  selectedOpenehrStatus,
  setSelectedOpenehrStatus,
  omopStatusCounts,
  openehrStatusCounts,
  nameView,
  setSelectedOmopStatus,
  onlyUnvalidatedAiSuggestions,
  setOnlyUnvalidatedAiSuggestions,
  selectedSourceSystems,
  setSelectedSourceSystems,
  allSourceSystems,
  handleSourceSystemToggle,
  allTags,
  popularTags,
  allStations,
  allStationsWithCategories,
  popularStations,
  savedSets,
  handleLoadSet,
  handleTagToggle,
  setTagDialogOpen,
}) => {
  const { t } = useTranslation();
  const [activeLevel, setActiveLevel] = useState(1); // 1 = type selection, 2 = filter options
  const [selectedFilterType, setSelectedFilterType] = useState(null);

  if (!open) return null;

  // Handle filter type selection
  const handleSelectFilterType = (filterType) => {
    setSelectedFilterType(filterType);
    setActiveLevel(2);
  };

  // Handle back to level 1
  const handleBack = () => {
    setActiveLevel(1);
    setSelectedFilterType(null);
  };

  // Get filter type configuration
  const filterTypes = [
    {
      id: 'categories',
      label: t('dataCatalog.filterPanel.filterTypes.categories'),
      icon: <LocalOfferIcon />,
      count: selectedTags.length,
    },
    {
      id: 'stations',
      label: t('dataCatalog.filterPanel.filterTypes.stations'),
      icon: <LocalHospitalIcon />,
      count: selectedStations.length,
    },
    {
      id: 'sourceSystems',
      label: t('dataCatalog.filterPanel.filterTypes.sourceSystems'),
      icon: <StorageIcon />,
      count: selectedSourceSystems.length,
    },
    {
      id: 'sensitivity',
      label: t('dataCatalog.filterPanel.filterTypes.sensitivity'),
      icon: <LockIcon />,
      count: selectedSensitivity && selectedSensitivity !== 'all' ? 1 : 0,
    },
    {
      id: 'omop',
      label: t('dataCatalog.filterPanel.filterTypes.omop'),
      icon: <CheckCircleIcon />,
      count: selectedOmopStatus && selectedOmopStatus !== 'all' ? 1 : 0,
    },
    // Die zweite Darstellung bekommt einen **eigenen** Eintrag (#782), keinen,
    // der dem Umschalter folgt. Nur so sind beide Statusbedingungen zugleich
    // setzbar -- und genau diese Kombination
    // (`openehr_status=validated & omop_status=not_mapped`) ist der Fall, um
    // den es im Vortrag geht.
    {
      id: 'openehr',
      label: t('dataCatalog.filterPanel.filterTypes.openehr'),
      icon: <CheckCircleIcon />,
      count: selectedOpenehrStatus && selectedOpenehrStatus !== 'all' ? 1 : 0,
    },
  ];

  // **Was die Ansicht erzwingt, muss die Maske auch zeigen** (#782,
  // Review Runde 1, B1). Vorher stand im Radio und im Chip weiter die Wahl des
  // Nutzers -- etwa "nicht gemappt" --, waehrend an die API
  // `validated,suggested` ging. Eine Maske, die etwas anderes behauptet als
  // das, was gefiltert wird, ist schlimmer als eine gesperrte Maske: sie
  // laesst den Nutzer eine Zahl fuer das Ergebnis seiner Auswahl halten.
  const omopErzwungen = Boolean(erzwungenerStatusfilter(nameView).omopStatus);
  const openehrErzwungen = Boolean(erzwungenerStatusfilter(nameView).openehrStatus);

  // Anzahl je Statuswert (#782 AK3c): `counts` ist `null`, solange die
  // Instanz die Aufschluesselung nicht liefert (#783) -- dann bleibt die
  // Beschriftung wie bisher, ohne eine erfundene Zahl. Ist `counts` da, zeigt
  // ein fehlender Schluessel 0, nicht nichts: eine leere Zeile waere hier die
  // falsche Aussage "gibt es nicht" statt der richtigen "gibt es hier keine".
  const mitAnzahl = (label, counts, wert) => (
    counts ? `${label} ${t('dataCatalog.filterPanel.statusCount', { count: counts.get(wert) ?? 0 })}` : label
  );

  // Get current filter config
  const currentFilter = filterTypes.find(f => f.id === selectedFilterType);

  // Get relevant saved sets for current filter type
  const getRelevantSets = () => {
    if (selectedFilterType === 'categories') {
      return savedSets?.filter(set => set.type === 'tags') || [];
    }
    if (selectedFilterType === 'stations') {
      return savedSets?.filter(set => set.type === 'stations') || [];
    }
    return [];
  };

  const relevantSets = getRelevantSets();

  return (
    <PanelContainer elevation={3}>
      {/* Header */}
      <Header>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {activeLevel === 2 && (
            <IconButton size="small" onClick={handleBack}>
              <ArrowBackIcon />
            </IconButton>
          )}
          {currentFilter ? (
            <>
              {currentFilter.icon}
              <Typography variant="subtitle1" fontWeight="bold">
                {currentFilter.label}
              </Typography>
            </>
          ) : (
            <Typography variant="subtitle1" fontWeight="bold">
              {t('dataCatalog.filterBar.addFilter')}
            </Typography>
          )}
        </Box>
        <IconButton size="small" onClick={onClose}>
          <CloseIcon />
        </IconButton>
      </Header>

      {/* Content */}
      <Content>
        {/* Level 1: Filter Type Selection */}
        {activeLevel === 1 && (
          <Box>
            <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
              {t('dataCatalog.filterPanel.selectType')}
            </Typography>
            <List disablePadding>
              {filterTypes.map((filterType) => (
                <ListItem key={filterType.id} disablePadding>
                  <ListItemButton onClick={() => handleSelectFilterType(filterType.id)}>
                    <ListItemIcon>{filterType.icon}</ListItemIcon>
                    <ListItemText
                      primary={filterType.label}
                      secondary={filterType.count > 0 ? t('dataCatalog.filterPanel.activeCount', { count: filterType.count }) : undefined}
                    />
                    <ChevronRightIcon />
                  </ListItemButton>
                </ListItem>
              ))}
            </List>
          </Box>
        )}

        {/* Level 2: Categories Filter */}
        {activeLevel === 2 && selectedFilterType === 'categories' && (
          <Box>
            <Alert severity="info" sx={{ mb: 2 }}>
              {t('dataCatalog.filterPanel.categoriesHelp')}
            </Alert>

            {/* Autocomplete for Tag Search */}
            <Autocomplete
              multiple
              options={allTags}
              value={selectedTags}
              onChange={(event, newValue) => setSelectedTags(newValue)}
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder={t('dataCatalog.filterPanel.categoriesSearch')}
                  size="medium"
                  InputProps={{
                    ...params.InputProps,
                    startAdornment: (
                      <>
                        <InputAdornment position="start">
                          <LocalOfferIcon fontSize="small" />
                        </InputAdornment>
                        {params.InputProps.startAdornment}
                      </>
                    ),
                  }}
                />
              )}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => (
                  <Chip
                    label={option}
                    size="small"
                    color="primary"
                    {...getTagProps({ index })}
                  />
                ))
              }
              sx={{ mb: 2 }}
            />

            {/* Saved Tag Sets */}
            {relevantSets.length > 0 && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="caption" color="textSecondary" sx={{ mb: 1, display: 'block' }}>
                  {t('dataCatalog.filterPanel.presetSets')}
                </Typography>
                <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                  {relevantSets.map(set => (
                    <Chip
                      key={set.id}
                      label={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <span>{set.icon}</span>
                          <span>{set.name}</span>
                        </Box>
                      }
                      onClick={() => {
                        handleLoadSet(set);
                      }}
                      color={set.color}
                      variant="outlined"
                      sx={{ cursor: 'pointer' }}
                    />
                  ))}
                </Box>
              </Box>
            )}

            {/* Popular Tags - Quick Selection */}
            <Box sx={{ mb: 2 }}>
              <Typography variant="caption" color="textSecondary" sx={{ mb: 1, display: 'block' }}>
                {t('dataCatalog.filterPanel.popularCategories')}
              </Typography>
              <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                {popularTags.map(tag => (
                  <Chip
                    key={tag}
                    label={tag}
                    size="medium"
                    onClick={() => handleTagToggle(tag)}
                    color={selectedTags.includes(tag) ? 'primary' : 'default'}
                    variant={selectedTags.includes(tag) ? 'filled' : 'outlined'}
                  />
                ))}
              </Box>
            </Box>

            {/* Manage Tags Link */}
            <Button
              variant="outlined"
              startIcon={<EditIcon />}
              onClick={() => {
                setTagDialogOpen(true);
                onClose();
              }}
              fullWidth
            >
              {t('dataCatalog.filterPanel.manageCategories')}
            </Button>
          </Box>
        )}

        {/* Level 2: Stations Filter */}
        {activeLevel === 2 && selectedFilterType === 'stations' && (
          <Box>
            <Alert severity="info" sx={{ mb: 2 }}>
              {t('dataCatalog.filterPanel.stationsHelp')}
            </Alert>

            {/* Autocomplete for Station Search with Grouping */}
            <Autocomplete
              multiple
              options={allStations}
              value={selectedStations}
              onChange={(event, newValue) => setSelectedStations(newValue)}
              groupBy={(option) => {
                const station = allStationsWithCategories.find(s => s.name === option);
                return station ? station.category : t('dataCatalog.frequency.stationGroups.other');
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder={t('dataCatalog.filterPanel.stationsSearch')}
                  size="medium"
                  InputProps={{
                    ...params.InputProps,
                    startAdornment: (
                      <>
                        <InputAdornment position="start">
                          <LocalHospitalIcon fontSize="small" />
                        </InputAdornment>
                        {params.InputProps.startAdornment}
                      </>
                    ),
                  }}
                />
              )}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => (
                  <Chip
                    label={option}
                    size="small"
                    color="secondary"
                    {...getTagProps({ index })}
                  />
                ))
              }
              renderOption={(props, option) => {
                const station = allStationsWithCategories.find(s => s.name === option);
                return (
                  <li {...props}>
                    <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                      <LocationOnIcon fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />
                      <Box sx={{ flexGrow: 1 }}>
                        <Typography variant="body2">{option}</Typography>
                        {station && station.building && (
                          <Typography variant="caption" color="textSecondary">
                            {station.building}
                          </Typography>
                        )}
                      </Box>
                    </Box>
                  </li>
                );
              }}
              sx={{ mb: 2 }}
            />

            {/* Saved Station Sets */}
            {relevantSets.length > 0 && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="caption" color="textSecondary" sx={{ mb: 1, display: 'block' }}>
                  {t('dataCatalog.filterPanel.presetSets')}
                </Typography>
                <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                  {relevantSets.map(set => (
                    <Chip
                      key={set.id}
                      label={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <span>{set.icon}</span>
                          <span>{set.name}</span>
                        </Box>
                      }
                      onClick={() => {
                        handleLoadSet(set);
                      }}
                      color={set.color}
                      variant="outlined"
                      sx={{ cursor: 'pointer' }}
                    />
                  ))}
                </Box>
              </Box>
            )}

            {/* Popular Stations - Quick Selection */}
            <Box>
              <Typography variant="caption" color="textSecondary" sx={{ mb: 1, display: 'block' }}>
                {t('dataCatalog.filterPanel.popularStations')}
              </Typography>
              <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                {popularStations.map(station => (
                  <Chip
                    key={station}
                    label={station}
                    size="medium"
                    onClick={() => {
                      setSelectedStations(prev =>
                        prev.includes(station) ? prev.filter(s => s !== station) : [...prev, station]
                      );
                    }}
                    color={selectedStations.includes(station) ? 'secondary' : 'default'}
                    variant={selectedStations.includes(station) ? 'filled' : 'outlined'}
                  />
                ))}
              </Box>
            </Box>
          </Box>
        )}

        {/* Level 2: Source Systems Filter */}
        {activeLevel === 2 && selectedFilterType === 'sourceSystems' && (
          <Box>
            <Alert severity="info" sx={{ mb: 2 }}>
              {t('dataCatalog.filterPanel.sourceSystemsHelp')}
            </Alert>

            {/* Autocomplete for Source System Search */}
            <Autocomplete
              multiple
              options={allSourceSystems}
              value={selectedSourceSystems}
              onChange={(event, newValue) => setSelectedSourceSystems(newValue)}
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder={t('dataCatalog.filterPanel.sourceSystemsSearch')}
                  size="medium"
                  InputProps={{
                    ...params.InputProps,
                    startAdornment: (
                      <>
                        <InputAdornment position="start">
                          <StorageIcon fontSize="small" />
                        </InputAdornment>
                        {params.InputProps.startAdornment}
                      </>
                    ),
                  }}
                />
              )}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => (
                  <Chip
                    label={option}
                    size="small"
                    color="info"
                    {...getTagProps({ index })}
                  />
                ))
              }
              sx={{ mb: 2 }}
            />

            {/* All Source Systems - Quick Selection */}
            <Box>
              <Typography variant="caption" color="textSecondary" sx={{ mb: 1, display: 'block' }}>
                {t('dataCatalog.filterPanel.availableSourceSystems')}
              </Typography>
              <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                {allSourceSystems.map(system => (
                  <Chip
                    key={system}
                    label={system}
                    size="medium"
                    onClick={() => handleSourceSystemToggle(system)}
                    color={selectedSourceSystems.includes(system) ? 'info' : 'default'}
                    variant={selectedSourceSystems.includes(system) ? 'filled' : 'outlined'}
                    icon={<StorageIcon />}
                  />
                ))}
              </Box>
            </Box>
          </Box>
        )}

        {/* Level 2: Sensitivity Filter */}
        {activeLevel === 2 && selectedFilterType === 'sensitivity' && (
          <Box>
            <Alert severity="info" sx={{ mb: 2 }}>
              {t('dataCatalog.filterPanel.sensitivityHelp')}
            </Alert>

            <FormControl component="fieldset" fullWidth>
              <RadioGroup
                value={selectedSensitivity}
                onChange={(e) => setSelectedSensitivity(e.target.value)}
              >
                <FormControlLabel
                  value="all"
                  control={<Radio />}
                  label={
                    <Box>
                      <Typography variant="body2">{t('dataCatalog.filterPanel.all')}</Typography>
                      <Typography variant="caption" color="textSecondary">
                        {t('dataCatalog.filterPanel.noRestriction')}
                      </Typography>
                    </Box>
                  }
                />
                <FormControlLabel
                  value="plain"
                  control={<Radio />}
                  label={
                    <Box>
                      <Typography variant="body2">{t('dataCatalog.filterPanel.sensitivityOptions.plain.label')}</Typography>
                      <Typography variant="caption" color="textSecondary">
                        {t('dataCatalog.filterPanel.sensitivityOptions.plain.description')}
                      </Typography>
                    </Box>
                  }
                />
                <FormControlLabel
                  value="pseudonymisiert"
                  control={<Radio />}
                  label={
                    <Box>
                      <Typography variant="body2">{t('dataCatalog.sensitivity.levels.pseudonymisiert')}</Typography>
                      <Typography variant="caption" color="textSecondary">
                        {t('dataCatalog.filterPanel.sensitivityOptions.pseudonymized.description')}
                      </Typography>
                    </Box>
                  }
                />
                <FormControlLabel
                  value="anonymisiert"
                  control={<Radio />}
                  label={
                    <Box>
                      <Typography variant="body2">{t('dataCatalog.sensitivity.levels.anonymisiert')}</Typography>
                      <Typography variant="caption" color="textSecondary">
                        {t('dataCatalog.filterPanel.sensitivityOptions.anonymized.description')}
                      </Typography>
                    </Box>
                  }
                />
              </RadioGroup>
            </FormControl>
          </Box>
        )}

        {/* Level 2: openEHR-Status (#782) */}
        {activeLevel === 2 && selectedFilterType === 'openehr' && (
          <Box>
            <Alert severity={openehrErzwungen ? 'warning' : 'info'} sx={{ mb: 2 }}>
              {openehrErzwungen
                ? t('dataCatalog.filterPanel.erzwungen')
                : t('dataCatalog.filterPanel.openehrHelp')}
            </Alert>

            <FormControl component="fieldset" fullWidth>
              <RadioGroup
                value={openehrErzwungen ? 'zugeordnet' : (selectedOpenehrStatus || 'all')}
                onChange={(e) => setSelectedOpenehrStatus(e.target.value)}
              >
                {openehrErzwungen && (
                  <FormControlLabel
                    value="zugeordnet"
                    disabled
                    control={<Radio />}
                    label={
                      <Box>
                        <Typography variant="body2">
                          {t('dataCatalog.filterPanel.zugeordnet')}
                        </Typography>
                        <Typography variant="caption" color="textSecondary">
                          {t('dataCatalog.filterPanel.zugeordnetBeschreibung')}
                        </Typography>
                      </Box>
                    }
                  />
                )}
                <FormControlLabel
                  value="all"
                  disabled={openehrErzwungen}
                  control={<Radio />}
                  label={
                    <Box>
                      <Typography variant="body2">{t('dataCatalog.filterPanel.all')}</Typography>
                      <Typography variant="caption" color="textSecondary">
                        {t('dataCatalog.filterPanel.noRestriction')}
                      </Typography>
                    </Box>
                  }
                />
                <FormControlLabel
                  value="validated"
                  control={<Radio />}
                  label={
                    <Box>
                      <Typography variant="body2">
                        {mitAnzahl(t('dataCatalog.omopStatus.validated'), openehrStatusCounts, 'validated')}
                      </Typography>
                      <Typography variant="caption" color="textSecondary">
                        {t('dataCatalog.filterPanel.openehrOptions.validated.description')}
                      </Typography>
                    </Box>
                  }
                />
                <FormControlLabel
                  value="suggested"
                  control={<Radio />}
                  label={
                    <Box>
                      <Typography variant="body2">
                        {mitAnzahl(t('dataCatalog.omopStatus.suggested'), openehrStatusCounts, 'suggested')}
                      </Typography>
                      <Typography variant="caption" color="textSecondary">
                        {t('dataCatalog.filterPanel.openehrOptions.suggested.description')}
                      </Typography>
                    </Box>
                  }
                />
                <FormControlLabel
                  value="not_mapped"
                  control={<Radio />}
                  label={
                    <Box>
                      <Typography variant="body2">
                        {mitAnzahl(t('dataCatalog.omopStatus.notMapped'), openehrStatusCounts, 'not_mapped')}
                      </Typography>
                      <Typography variant="caption" color="textSecondary">
                        {t('dataCatalog.filterPanel.openehrOptions.notMapped.description')}
                      </Typography>
                    </Box>
                  }
                />
              </RadioGroup>
            </FormControl>
          </Box>
        )}

        {/* Level 2: OMOP Status Filter */}
        {activeLevel === 2 && selectedFilterType === 'omop' && (
          <Box>
            <Alert severity={omopErzwungen ? 'warning' : 'info'} sx={{ mb: 2 }}>
              {omopErzwungen
                ? t('dataCatalog.filterPanel.erzwungen')
                : t('dataCatalog.filterPanel.omopHelp')}
            </Alert>

            <FormControl component="fieldset" fullWidth>
              <RadioGroup
                value={omopErzwungen ? 'zugeordnet' : (selectedOmopStatus || 'all')}
                onChange={(e) => setSelectedOmopStatus(e.target.value)}
              >
                {omopErzwungen && (
                  <FormControlLabel
                    value="zugeordnet"
                    disabled
                    control={<Radio />}
                    label={
                      <Box>
                        <Typography variant="body2">
                          {t('dataCatalog.filterPanel.zugeordnet')}
                        </Typography>
                        <Typography variant="caption" color="textSecondary">
                          {t('dataCatalog.filterPanel.zugeordnetBeschreibung')}
                        </Typography>
                      </Box>
                    }
                  />
                )}
                <FormControlLabel
                  value="all"
                  disabled={omopErzwungen}
                  control={<Radio />}
                  label={
                    <Box>
                      <Typography variant="body2">{t('dataCatalog.filterPanel.all')}</Typography>
                      <Typography variant="caption" color="textSecondary">
                        {t('dataCatalog.filterPanel.noRestriction')}
                      </Typography>
                    </Box>
                  }
                />
                <FormControlLabel
                  value="validated"
                  disabled={omopErzwungen}
                  control={<Radio />}
                  label={
                    <Box>
                      <Typography variant="body2">
                        {mitAnzahl(t('dataCatalog.omopStatus.validated'), omopStatusCounts, 'validated')}
                      </Typography>
                      <Typography variant="caption" color="textSecondary">
                        {t('dataCatalog.filterPanel.omopOptions.validated.description')}
                      </Typography>
                    </Box>
                  }
                />
                <FormControlLabel
                  value="suggested"
                  control={<Radio />}
                  label={
                    <Box>
                      <Typography variant="body2">
                        {mitAnzahl(t('dataCatalog.omopStatus.suggested'), omopStatusCounts, 'suggested')}
                      </Typography>
                      <Typography variant="caption" color="textSecondary">
                        {t('dataCatalog.filterPanel.omopOptions.suggested.description')}
                      </Typography>
                    </Box>
                  }
                />
                <FormControlLabel
                  value="not_mapped"
                  control={<Radio />}
                  label={
                    <Box>
                      <Typography variant="body2">
                        {mitAnzahl(t('dataCatalog.omopStatus.notMapped'), omopStatusCounts, 'not_mapped')}
                      </Typography>
                      <Typography variant="caption" color="textSecondary">
                        {t('dataCatalog.filterPanel.omopOptions.notMapped.description')}
                      </Typography>
                    </Box>
                  }
                />
              </RadioGroup>
            </FormControl>

            {/* Arbeitsliste #257 AK4: "was die KI vorgeschlagen und niemand
                geprueft hat" -- eigener Schalter, weil das die Herkunft
                (#257) und nicht den Workflow-Status filtert. */}
            <Divider sx={{ my: 2 }} />
            <FormControlLabel
              control={
                <Checkbox
                  checked={onlyUnvalidatedAiSuggestions}
                  onChange={(e) => setOnlyUnvalidatedAiSuggestions(e.target.checked)}
                />
              }
              label={
                <Box>
                  <Typography variant="body2">
                    {t('dataCatalog.mappingProvenance.filterLabel', 'Nur ungeprüfte KI-Vorschläge')}
                  </Typography>
                  <Typography variant="caption" color="textSecondary">
                    {t(
                      'dataCatalog.mappingProvenance.filterHelp',
                      'Zeigt nur Karten, die die KI vorgeschlagen hat und die noch niemand geprüft hat.'
                    )}
                  </Typography>
                </Box>
              }
            />
          </Box>
        )}
      </Content>
    </PanelContainer>
  );
};

export default FilterExpansionPanel;
